import { useState } from 'react'
import PropTypes from 'prop-types'
import { FieldArray } from 'formik'
import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'
import Chip from '@mui/material/Chip'
import Avatar from '@mui/material/Avatar'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import AppPropTypes from '../../shared/appPropTypes'
import { getNameInitials } from '../../helpers/common'
import { ROLE_TYPES } from '../../helpers/project'
import { getMemberType } from '../../helpers/memberTypes'
import { useIntl } from 'react-intl'
import Tooltip from '@mui/material/Tooltip'
import withFormik from './withFormik'
import SelectField from './SelectField'
import { Typography } from '@mui/material'

const ProjectMembersField = ({ name, loading, value, disabled }) => {
  const members = uniqBy(value, m => m.userId || m.id)

  return loading ? (
    <div style={{ textAlign: 'center', padding: '1em' }}>
      <CircularProgress />
    </div>
  ) : (
    <FieldArray name={name}>
      {arrayHelpers => (
        <>
          <Typography
            variant="body2"
            style={{ fontWeight: 'bold', marginBottom: 8 }}
          >
            Members
          </Typography>
          {members.length === 0 ? (
            <Typography variant="body2" style={{ color: '#86A2B3' }}>
              No members available for this folder
            </Typography>
          ) : (
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              style={{ rowGap: 8 }}
            >
              {map(members, (member, index) => (
                <MemberListItem
                  key={member.userId || member.id || index}
                  name={name}
                  disabled={disabled}
                  index={index}
                  member={member}
                  onDelete={() => arrayHelpers.remove(index)}
                />
              ))}
            </Grid>
          )}
        </>
      )}
    </FieldArray>
  )
}

ProjectMembersField.propTypes = {
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  value: PropTypes.arrayOf(AppPropTypes.projectMember)
}

const MemberListItem = ({ name, index, member, disabled, onDelete }) => {
  const intl = useIntl()
  const { name: displayName, role } = member
  const initials = getNameInitials(displayName)
  const roleFieldName = `${name}[${index}].role`
  const [confirmOpen, setConfirmOpen] = useState(false)

  const handleDeleteClick = () => setConfirmOpen(true)
  const handleConfirm = () => {
    setConfirmOpen(false)
    onDelete()
  }
  const handleCancel = () => setConfirmOpen(false)

  const confirmDialog = (
    <Dialog open={confirmOpen} onClose={handleCancel}>
      <DialogTitle>Remove Member</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to remove{' '}
          <strong>{displayName || 'this user'}</strong> from this folder?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="secondary">
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  )

  const roleDisplay = disabled ? (
    <Typography variant="body2" style={{ minWidth: 90, textAlign: 'right' }}>
      {role}
    </Typography>
  ) : (
    <SelectField
      name={roleFieldName}
      value={role}
      disabled={false}
      valueField="name"
      primaryField="name"
      items={map(Object.values(ROLE_TYPES), role => ({ name: role }))}
      required
      fullWidth
    />
  )

  const memberType = getMemberType(member.principalType)

  const tooltip = memberType
    ? intl.formatMessage({ id: memberType.labelId })
    : member.email
    ? 'Email: ' + member.email
    : ''

  const row = (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      style={{ maxHeight: 60 }}
    >
      <Grid item>
        <Chip
          style={{ maxWidth: 150 }}
          avatar={<Avatar>{memberType ? memberType.icon : initials}</Avatar>}
          label={displayName ? displayName : 'Deleted User'}
          onDelete={disabled ? undefined : handleDeleteClick}
        />
      </Grid>
      <Grid item>{roleDisplay}</Grid>
    </Grid>
  )

  return (
    <>
      {confirmDialog}
      {tooltip ? (
        <Tooltip title={tooltip} placement="bottom-start">
          {row}
        </Tooltip>
      ) : (
        row
      )}
    </>
  )
}

MemberListItem.propTypes = {
  disabled: PropTypes.bool,
  index: PropTypes.number.isRequired,
  member: AppPropTypes.projectMember,
  name: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired
}

export default withFormik(ProjectMembersField)
